// Cuánto MIDE un texto en Helvetica, para poder cortar líneas —
// ADR-2026-09-08-el-agente-lee-y-crea-pdfs.
//
// Un PDF no sabe de "ancho de caja": vos escribís texto en una coordenada y si te pasás,
// se sale de la hoja. Así que el corte de líneas lo hacemos nosotros, y para eso hace falta
// el ancho real de cada carácter.
//
// Los números son los de las métricas de Helvetica (unidades de 1/1000 em, que es como el
// PDF mide todo). Las letras acentuadas miden lo MISMO que su letra base en Helvetica —
// una `á` mide igual que una `a`—, así que la tabla se arma sobre ASCII y el resto cae por
// esa regla en vez de repetir 130 números que serían los mismos.
//
// Todo puro.

/** Anchos de Helvetica regular, código ASCII 32..126, en 1/1000 em. */
const HELVETICA = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, // 32-47
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556, // 48-63
  1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778, // 64-79
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, // 80-95
  333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556, // 96-111
  556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584, // 112-126
];

/** Anchos de Helvetica-Bold, mismo rango. */
const HELVETICA_BOLD = [
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278, // 32-47
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611, // 48-63
  975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778, // 64-79
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556, // 80-95
  333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611, // 96-111
  611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584, // 112-126
];

/**
 * La letra base de un carácter acentuado. En Helvetica una `á` mide como una `a`, y una
 * `Ñ` como una `N`: con esto la tabla ASCII alcanza para todo el español.
 */
const BASE: Record<string, string> = {
  á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u', ü: 'u', ñ: 'n', à: 'a', è: 'e', ì: 'i',
  ò: 'o', ù: 'u', â: 'a', ê: 'e', î: 'i', ô: 'o', û: 'u', ä: 'a', ë: 'e', ï: 'i',
  ö: 'o', ç: 'c', Á: 'A', É: 'E', Í: 'I', Ó: 'O', Ú: 'U', Ü: 'U', Ñ: 'N', À: 'A',
  È: 'E', Ì: 'I', Ò: 'O', Ù: 'U', Ç: 'C',
};

/** Ancho de UN carácter, en 1/1000 em. Lo desconocido se estima como una `o`. */
function anchoDeChar(c: string, negrita: boolean): number {
  const tabla = negrita ? HELVETICA_BOLD : HELVETICA;
  const base = BASE[c] ?? c;
  const cod = base.charCodeAt(0);
  if (cod >= 32 && cod <= 126) return tabla[cod - 32];
  // Los pocos símbolos de Latin-1 que quedan afuera (°, €, ¿, ¡…) rondan el ancho de una
  // `o`. Preferimos estimar de más: una línea que corta antes se ve bien; una que se pasa,
  // no.
  return tabla[111 - 32];
}

/** Ancho del texto en PUNTOS, al tamaño dado. */
export function anchoDeTexto(texto: string, tamano: number, negrita = false): number {
  let total = 0;
  for (const c of texto) total += anchoDeChar(c, negrita);
  return (total * tamano) / 1000;
}

/**
 * Corta un texto en líneas que entren en `anchoMax` (puntos). Corta por palabras; una
 * palabra sola más larga que la caja se parte por caracteres, porque si no se saldría de
 * la hoja sin que nada avise (una URL larga es el caso típico).
 *
 * Los saltos de línea del texto original se respetan: son párrafos que el agente escribió
 * a propósito.
 */
export function partirEnLineas(
  texto: string,
  anchoMax: number,
  tamano: number,
  negrita = false,
): string[] {
  const out: string[] = [];
  for (const parrafo of (texto ?? '').split('\n')) {
    const palabras = parrafo.split(/\s+/).filter(Boolean);
    if (palabras.length === 0) {
      out.push('');
      continue;
    }
    let linea = '';
    for (const palabra of palabras) {
      const tentativa = linea ? `${linea} ${palabra}` : palabra;
      if (anchoDeTexto(tentativa, tamano, negrita) <= anchoMax) {
        linea = tentativa;
        continue;
      }
      if (linea) out.push(linea);
      // La palabra sola no entra: se parte por caracteres.
      if (anchoDeTexto(palabra, tamano, negrita) > anchoMax) {
        let trozo = '';
        for (const c of palabra) {
          if (anchoDeTexto(trozo + c, tamano, negrita) > anchoMax && trozo) {
            out.push(trozo);
            trozo = c;
          } else {
            trozo += c;
          }
        }
        linea = trozo;
      } else {
        linea = palabra;
      }
    }
    if (linea) out.push(linea);
  }
  return out;
}

/** Recorta un texto a lo que entre en una celda, con puntos suspensivos. */
export function recortarALoAncho(
  texto: string,
  anchoMax: number,
  tamano: number,
  negrita = false,
): string {
  const limpio = (texto ?? '').replace(/\s+/g, ' ').trim();
  if (anchoDeTexto(limpio, tamano, negrita) <= anchoMax) return limpio;
  const puntos = anchoDeTexto('…', tamano, negrita);
  let out = '';
  for (const c of limpio) {
    if (anchoDeTexto(out + c, tamano, negrita) + puntos > anchoMax) break;
    out += c;
  }
  return `${out}…`;
}
